import { useEffect } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EmitterListener = (...args: any[]) => void;

export function createEmitter<L extends EmitterListener>() {
    const listeners: L[] = [];

    function subscribe(listener: L) {
        if (!listeners.includes(listener)) {
            listeners.push(listener);
        }
    }

    function unsubscribe(listener: L) {
        const index = listeners.indexOf(listener);
        if (index > -1) {
            listeners.splice(index, 1);
        }
    }

    function emit(...args: Parameters<L>) {
        for (const listener of listeners) {
            listener(...args);
        }
    }

    return {
        listeners: [] as L[],
        subscribe,
        unsubscribe,
        emit,
    };
}

export type Emitter<L extends EmitterListener> = ReturnType<
    typeof createEmitter<L>
>;

export function useEmitter<L extends EmitterListener>(
    emitter: Emitter<L>,
    callback: L
) {
    useEffect(() => {
        if (!emitter || !callback) return;

        let mounted = true;
        const emitWhenMounted: L = ((...args: Parameters<L>) => {
            mounted && callback(...args);
        }) as L;
        emitter.subscribe(emitWhenMounted);
        return () => {
            mounted = false;
            emitter.unsubscribe(emitWhenMounted);
        };
    }, [emitter, callback]);
}
